/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-no-constructed-context-values */
/* eslint-disable import/no-extraneous-dependencies */
import "./styles/style.css";

import { MsalProvider } from "@azure/msal-react";
import { CacheProvider, EmotionCache } from "@emotion/react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { QueryClientProvider } from "@tanstack/react-query";
import { SnackbarProvider, SnackbarProviderProps } from "notistack";
import { useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Provider, useDispatch, useSelector } from "react-redux";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";

import {
  getListPrivilegeByRole,
  getRoleNameProfileSA,
} from "./components/container/login/api-endpoint/callApi";
import { LayoutPrivateRoute } from "./components/layout/private-routes";
import { LayoutPublicRoute } from "./components/layout/public-routes";
import Loading from "./components/loading/loading";
import getQueryClient from "./config/react-query";
import Keys from "./constants/Keys";
import { UseModalProvider } from "./hooks/use-modal/provider";
import { Maintenance } from "./pages/maintenanace";
import { protectedRoutes } from "./routes/protected";
import { publicRoutes } from "./routes/public";
import { persistor, store } from "./store";
import { selectUserProfile } from "./store/auth/selector";
import { setUserRole } from "./store/auth/slice";
import { themes } from "./styles/mui/theme";
import createEmotionCache from "./utils/emotion-cache";
import { msalInstance } from "./utils/new-azure-ad";
import { getAuthToken, setLastVisitedPage } from "./utils/storage";

interface CustomAppProps {
  emotionCache?: EmotionCache;
}

const clientSideEmotionCache = createEmotionCache();

const queryClient = getQueryClient();
const snackbarConfig: SnackbarProviderProps = {
  anchorOrigin: {
    vertical: "top",
    horizontal: "right",
  },
  autoHideDuration: 5000,
  style: {
    maxWidth: 300,
  },
};

function LocationTracker() {
  const location = useLocation();

  useEffect(() => {
    const includedPaths = new Set(
      protectedRoutes
        .map((route) => route.path)
        .filter((path): path is string => typeof path === "string")
    );

    if (includedPaths.has(location.pathname)) {
      setLastVisitedPage(location.pathname);
    }
  }, [location.pathname]);

  return null;
}

function App({ emotionCache = clientSideEmotionCache }: CustomAppProps) {
  return (
    <BrowserRouter>
      <AllProvider emotionCache={emotionCache}>
        <CssBaseline />
        {Keys.IsMaintenance ? (
          <Maintenance />
        ) : (
          <>
            <LocationTracker />
            <Routing />
          </>
        )}
      </AllProvider>
    </BrowserRouter>
  );
}

export function AllProvider({
  children,
  emotionCache,
}: {
  children: JSX.Element[];
  emotionCache: EmotionCache;
}) {
  return (
    <QueryClientProvider client={queryClient}>
      <MsalProvider instance={msalInstance}>
        <CacheProvider value={emotionCache}>
          <HelmetProvider>
            <Provider store={store}>
              <PersistGate loading={null} persistor={persistor}>
                <SnackbarProvider
                  anchorOrigin={snackbarConfig.anchorOrigin}
                  autoHideDuration={snackbarConfig.autoHideDuration}
                  style={snackbarConfig.style}
                >
                  <ThemeProvider theme={themes}>
                    <UseModalProvider>{children}</UseModalProvider>
                  </ThemeProvider>
                </SnackbarProvider>
              </PersistGate>
            </Provider>
          </HelmetProvider>
        </CacheProvider>
      </MsalProvider>
    </QueryClientProvider>
  );
}

function Routing() {
  const token = getAuthToken();
  const user = useSelector(selectUserProfile);
  const dispatch = useDispatch();
  const [menuItemsToAccess, setMenuItemsToAccess] = useState<string[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!token || !user?.Email || !user?.BinusianId || isLoaded) {
      setIsLoading(false);
      return;
    }

    const fetchApiData = async () => {
      try {
        console.log("Fetching data for user:", user.Email);

        const [getListPrivilege, getRoleProfile] = await Promise.all([
          getListPrivilegeByRole(user.Email),
          getRoleNameProfileSA(user.BinusianId),
        ]);

        await Promise.all([
          dispatch(setUserRole(getRoleProfile)),
          setMenuItemsToAccess(getListPrivilege),
        ]);
      } catch (error) {
        console.log("Error fetching data:", error);
      } finally {
        setIsLoaded(true);
        setIsLoading(false);
      }
    };

    fetchApiData();
  }, [token, user?.Email, user?.BinusianId]);

  if (isLoading) {
    return <Loading open={isLoading} />;
  }

  return (
    <Routes>
      {/* Public routes */}
      {publicRoutes.map((route) => (
        <Route
          path={route.path}
          element={
            route.noLayout ? (
              <route.component />
            ) : (
              <LayoutPublicRoute>
                <route.component />
              </LayoutPublicRoute>
            )
          }
          key={route.key}
        />
      ))}

      {/* Protected routes */}
      {protectedRoutes
        .filter(
          (route) => route.access && menuItemsToAccess.includes(route.access)
        )
        .map((route) => (
          <Route
            path={route.path}
            element={
              token ? (
                <LayoutPrivateRoute>
                  <route.component />
                </LayoutPrivateRoute>
              ) : (
                <Navigate to="/login" replace />
              )
            }
            key={route.key}
          />
        ))}

      {/* Catch-all for non-logged-in users */}
      {token ? (
        <Route
          path="/asal-aja-biar-gak-error"
          element={<Navigate to="/" replace />}
        />
      ) : (
        <Route path="*" element={<Navigate to="/login" replace />} />
      )}
    </Routes>
  );
}

export default App;
